import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Link, Divider,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../hooks/useNotifications';
import { loginScreenStyles } from './LoginScreen.styles';
import { t } from '../../utils/translations';

const LoginScreen: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { login, isLoggingIn } = useAuth();
  const { showError, showSuccess } = useNotifications();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim() || !password.trim()) {
      showError(t('login.fillAllFields'), t('login.missingData'));
      return;
    }

    try {
      await login(email, password);
      showSuccess(t('login.loginSuccess'), t('login.welcome'));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('login.unexpectedError');
      showError(errorMessage, t('login.loginError'));
    }
  };

  return (
    <Container maxWidth="sm" sx={loginScreenStyles.container}>
      <Paper elevation={3} sx={loginScreenStyles.paper}>
        <Box sx={loginScreenStyles.content}>
          <Typography variant="h4" component="h1" color={"primary"} sx={loginScreenStyles.title}>
            Listum
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={loginScreenStyles.form}>
            <TextField
              fullWidth
              label={t('login.email')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              sx={loginScreenStyles.input}
            />

            <TextField
              fullWidth
              label={t('login.password')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              sx={loginScreenStyles.input}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoggingIn}
              sx={loginScreenStyles.submitButton}
            >
              {isLoggingIn ? <CircularProgress size={24} /> : t('login.loginButton')}
            </Button>

            <Box sx={loginScreenStyles.registerLink}>
              <Typography variant="body2">
                {t('login.noAccount')}{' '}
                <Link href="/register" underline="hover">
                  {t('login.registerLink')}
                </Link>
              </Typography>
              <Divider sx={{ mt: 2 }} />
              <Typography variant="body2" sx={{ mt: 2 }}>
                <Link href="/forgot-password" underline="hover">
                  {t('login.forgotPassword')}
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ mt: 2 }}>
                <Link href="/resend-activation-email" underline="hover">
                  {t('login.resendActivationLink')}
                </Link>
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default LoginScreen;